import React, { ChangeEvent, useState } from 'react';
import { Button, message } from 'antd';
import { ClockCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import InputFilterItem from "../../../../delivery/components/InputFilterItem";
import { VirtualAccountFilterType } from '../graphql/types/VirtualAccountType';
import { ChannelSelectFilter } from '../../../../../components/settlement/ChannelSelectFilter';
import ParcelsDownloadModal from "../../components/ParcelsDownloadModal";
import { CREATE_VIRTUAL_ACCOUNT_DOWNLOAD_TASK, VirtualAccountDownloadTaskResponse } from '../graphql/mutations/createVirtualAccountDownloadTask';
import { HasResource } from 'components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

interface VirtualAccountFilterProps {
    filters: VirtualAccountFilterType;
    setFilters: (filters: VirtualAccountFilterType) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
}

const VirtualAccountFilter: React.FC<VirtualAccountFilterProps> = ({
    filters,
    setFilters,
    handleFilter,
    handleResetFilters,
}) => {
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
    const DOWNLOAD_COOLDOWN = 5000; // 5秒冷却时间

    // 下载完成模态框状态
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState<string>('');

    // 使用mutation来创建下载任务
    const [createVirtualAccountDownloadTask] = useMutation<VirtualAccountDownloadTaskResponse>(CREATE_VIRTUAL_ACCOUNT_DOWNLOAD_TASK);

    // 从localStorage获取上次下载时间
    const getLastDownloadTime = (): number => {
        const stored = localStorage.getItem('virtualAccountDownloadLastTime');
        return stored ? parseInt(stored, 10) : 0;
    };

    const setLastDownloadTime = (time: number) => {
        localStorage.setItem('virtualAccountDownloadLastTime', time.toString());
    };

    // 冷却时间倒计时
    React.useEffect(() => {
        const checkCooldown = () => {
            const lastTime = getLastDownloadTime();
            if (lastTime > 0) {
                const currentTime = Date.now();
                const remaining = Math.max(0, DOWNLOAD_COOLDOWN - (currentTime - lastTime));
                const remainingSeconds = Math.ceil(remaining / 1000);
                setCooldownRemaining(remainingSeconds);

                if (remaining <= 0) {
                    setCooldownRemaining(0);
                    setDownloadLoading(false);
                    localStorage.removeItem('virtualAccountDownloadLastTime');
                }
            } else {
                setCooldownRemaining(0);
            }
        };

        checkCooldown();
        const interval = setInterval(checkCooldown, 1000);
        return () => clearInterval(interval);
    }, [DOWNLOAD_COOLDOWN]);

    // Download handler function
    const handleDownloadVirtualAccount = async () => {
        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();

        // Check cooldown time
        if (currentTime - lastTime < DOWNLOAD_COOLDOWN) {
            const remainingTime = Math.ceil((DOWNLOAD_COOLDOWN - (currentTime - lastTime)) / 1000);
            message.warning(`Please wait ${remainingTime} seconds before trying again`);
            return;
        }

        if (downloadLoading) {
            message.warning('File is being generated, please do not click repeatedly');
            return;
        }

        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime);

            const response = await createVirtualAccountDownloadTask({
                variables: {
                    filters: filters,
                }
            });

            if (response.data?.createVirtualAccountDownloadTask) {
                setGeneratedFileName(response.data?.createVirtualAccountDownloadTask);
                setDownloadCompleteModalVisible(true);
            } else {
                message.error('Failed to start export task');
            }
        } catch (error) {
            console.error('Create Virtual Account Download Task Failed:', error);
            message.error('Export failed, please try again later');
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="mb-6">
            <div className="grid grid-cols-3 gap-x-4 gap-y-3 mb-4">
                <ChannelSelectFilter
                    label="客户名称"
                    value={filters.clientId ?? undefined}
                    onChange={(value: string | undefined) => setFilters({...filters, clientId: value})}
                    spanClassName="w-[9em]"
                />
                <InputFilterItem
                    label="账户ID"
                    value={filters.virtualAccountId || undefined}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, virtualAccountId: e.target.value || undefined })}
                    spanClassName="w-[9em]"
                />
            </div>

            <div className="mb-4 flex justify-between">
                <div className="space-x-4">
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REALTIME_BALANCE]}>
                        <Button
                            type="primary"
                            onClick={handleDownloadVirtualAccount}
                            loading={cooldownRemaining > 0}
                            disabled={downloadLoading || cooldownRemaining > 0}
                            icon={cooldownRemaining > 0 ? <ClockCircleOutlined/> : <DownloadOutlined/>}
                        >
                            {cooldownRemaining > 0
                                ? `Download ${cooldownRemaining}s`
                                : 'Download'
                            }
                        </Button>
                    </HasResource>
                    <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_REALTIME_BALANCE]}>
                        <Button type="primary" onClick={handleFilter}>
                            Filter
                        </Button>
                    </HasResource>
                    <Button onClick={handleResetFilters}>
                        Reset
                    </Button>
                </div>
            </div>

            <ParcelsDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default VirtualAccountFilter;
